// src/components/TherapistKnowledge.js - Knowledge base management for therapists
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Icon from './Icon';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const TherapistKnowledge = ({ token }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('instructions');
  const [knowledgeEntries, setKnowledgeEntries] = useState([]);
  const [aiInstructions, setAiInstructions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingInstruction, setEditingInstruction] = useState(null);
  const [viewingContent, setViewingContent] = useState(null);

  // Form states
  const [knowledgeForm, setKnowledgeForm] = useState({
    title: '',
    description: '',
    content: '',
    category: 'general',
    priority: 1,
    file: null
  });

  const [instructionForm, setInstructionForm] = useState({
    instruction_type: 'personality',
    instruction_text: '',
    priority: 1
  });

  useEffect(() => {
    loadData();
  }, [activeTab]);

  // Handle escape key to close modal and body scroll lock
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && viewingContent) {
        setViewingContent(null);
      }
    };

    if (viewingContent) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleEscapeKey);
    } else {
      // Restore body scroll when modal is closed
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [viewingContent]);

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      if (activeTab === 'knowledge') {
        await loadKnowledgeEntries();
      } else if (activeTab === 'instructions') {
        await loadAiInstructions();
      }
    } catch (err) {
      setError('Kunde inte ladda data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadKnowledgeEntries = async () => {
    const response = await axios.get(`${API_BASE}/api/therapist-knowledge/knowledge`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    setKnowledgeEntries(response.data);
  };

  const loadAiInstructions = async () => {
    const response = await axios.get(`${API_BASE}/api/therapist-knowledge/instructions`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    setAiInstructions(response.data);
  };

  const handleCreateKnowledge = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (editingItem) {
        // Update existing knowledge (only text content, no file upload for edits)
        await axios.put(
          `${API_BASE}/api/therapist-knowledge/knowledge/${editingItem.id}`,
          {
            title: knowledgeForm.title,
            description: knowledgeForm.description,
            content: knowledgeForm.content,
            category: knowledgeForm.category,
            priority: knowledgeForm.priority
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        setSuccess('Kunskapsbas uppdaterad');
      } else {
        // Create new knowledge - check if file upload or text content
        if (knowledgeForm.file) {
          // File upload mode
          const formData = new FormData();
          formData.append('title', knowledgeForm.title);
          formData.append('description', knowledgeForm.description);
          formData.append('category', knowledgeForm.category);
          formData.append('priority', knowledgeForm.priority);
          formData.append('file', knowledgeForm.file);

          await axios.post(
            `${API_BASE}/api/therapist-knowledge/upload-document`,
            formData,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              }
            }
          );
          setSuccess('Dokument uppladdat och kunskapsbas skapad');
        } else {
          // Text content mode
          await axios.post(
            `${API_BASE}/api/therapist-knowledge/knowledge`,
            {
              title: knowledgeForm.title,
              description: knowledgeForm.description,
              content: knowledgeForm.content,
              category: knowledgeForm.category,
              priority: knowledgeForm.priority
            },
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          setSuccess('Kunskapsbas skapad');
        }
      }

      setKnowledgeForm({ title: '', description: '', content: '', category: 'general', priority: 1, file: null });
      setShowAddForm(false);
      setEditingItem(null);
      loadKnowledgeEntries();
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunde inte spara kunskapsbas');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInstruction = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (editingInstruction && editingInstruction.id) {
        // Update existing instruction using PUT
        await axios.put(
          `${API_BASE}/api/therapist-knowledge/instructions/${editingInstruction.id}`,
          {
            instruction_text: instructionForm.instruction_text,
            priority: instructionForm.priority
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        setSuccess('AI-instruktion uppdaterad');
      } else {
        // Create new instruction
        await axios.post(
          `${API_BASE}/api/therapist-knowledge/instructions`,
          instructionForm,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        setSuccess('AI-instruktion sparad');
      }
      
      // Reset form and states
      setInstructionForm({ instruction_type: 'personality', instruction_text: '', priority: 1 });
      setEditingInstruction(null);
      loadAiInstructions();
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunde inte spara instruktion');
    } finally {
      setLoading(false);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      // Check file type
      const allowedTypes = ['.txt', '.pdf', '.doc', '.docx'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      
      if (allowedTypes.includes(fileExtension)) {
        setKnowledgeForm({...knowledgeForm, file: file});
        setError(''); // Clear any previous errors
      } else {
        setError('Filtypen stöds inte. Välj en TXT, PDF, DOC eller DOCX-fil.');
      }
    }
  };

  const handleDeleteKnowledge = async (id) => {
    if (!window.confirm('Är du säker på att du vill ta bort denna kunskapsbas?')) return;

    try {
      await axios.delete(`${API_BASE}/api/therapist-knowledge/knowledge/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSuccess('Kunskapsbas borttagen');
      loadKnowledgeEntries();
    } catch (err) {
      setError('Kunde inte ta bort kunskapsbas');
    }
  };

  const handleEditKnowledge = (knowledge) => {
    setKnowledgeForm({
      title: knowledge.title,
      description: knowledge.description || '',
      content: knowledge.content,
      category: knowledge.category,
      priority: knowledge.priority,
      file: null // No file when editing existing knowledge
    });
    setEditingItem(knowledge);
    setShowAddForm(true);
  };

  const handleViewContent = (knowledge) => {
    setViewingContent(knowledge);
  };

  const handleEditInstruction = (instruction) => {
    setInstructionForm({
      instruction_type: instruction.instruction_type,
      instruction_text: instruction.instruction_text,
      priority: instruction.priority
    });
    setEditingInstruction(instruction);
  };

  const handleDeleteInstruction = async (id, instructionType) => {
    if (!window.confirm(`Är du säker på att du vill ta bort instruktionen för "${getInstructionTypeInfo(instructionType).label}"?`)) return;

    try {
      await axios.delete(`${API_BASE}/api/therapist-knowledge/instructions/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSuccess('AI-instruktion borttagen');
      loadAiInstructions();
    } catch (err) {
      setError('Kunde inte ta bort instruktion');
    }
  };

  const handleCreateInstructionFromTemplate = async (instructionType, instruction) => {
    try {
      await axios.post(
        `${API_BASE}/api/therapist-knowledge/instructions`,
        {
          instruction_type: instructionType,
          instruction_text: instruction.text,
          priority: instruction.priority
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
    } catch (err) {
      console.error('Error creating instruction from template:', err);
    }
  };

  const handleUpdateInstructionFromTemplate = async (id, instruction) => {
    try {
      await axios.put(
        `${API_BASE}/api/therapist-knowledge/instructions/${id}`,
        {
          instruction_text: instruction.text,
          priority: instruction.priority
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
    } catch (err) {
      console.error('Error updating instruction from template:', err);
    }
  };

  const getCategoryInfo = (category) => {
    const categories = {
      'therapy_approach': { label: 'Terapiansats', icon: 'compass', color: 'blue' },
      'techniques': { label: 'Tekniker', icon: 'wrench', color: 'green' },
      'guidelines': { label: 'Riktlinjer', icon: 'shield', color: 'red' },
      'general': { label: 'Allmänt', icon: 'square-library', color: 'gray' }
    };
    return categories[category] || categories['general'];
  };

  const getInstructionTypeInfo = (type) => {
    const types = {
      'personality': { label: 'Personlighet', icon: 'smile', description: 'Hur AI:n ska kommunicera' },
      'approach': { label: 'Ansats', icon: 'compass', description: 'Terapeutisk inriktning' },
      'boundaries': { label: 'Gränser', icon: 'shield', description: 'Viktiga begränsningar' },
      'techniques': { label: 'Tekniker', icon: 'wrench', description: 'Specifika metoder att använda' }
    };
    return types[type] || types['personality'];
  };

  const getTherapyTemplates = () => {
    return {
      'cbt': {
        name: 'Kognitiv Beteendeterapi (CBT)',
        description: 'Fokuserar på tankar, känslor och beteenden samt deras kopplingar',
        instructions: {
          personality: {
            text: 'Var strukturerad, målinriktad och kollaborativ. Använd en varm men professionell ton. Ställ tydliga frågor och uppmuntra reflektion över tanke-känsla-beteende kopplingar.',
            priority: 8
          },
          approach: {
            text: 'Använd CBT-principer: identifiera negativa tankemönster, utmana irrationella tankar, och fokusera på här-och-nu problem. Strukturera sessioner med agenda och hemuppgifter.',
            priority: 9
          },
          boundaries: {
            text: 'Håll fokus på nutida problem snarare än djup utforskning av det förflutna. Undvik att ge direkta råd - väglед istället klienten att upptäcka egna lösningar.',
            priority: 7
          },
          techniques: {
            text: 'Använd sokratisk dialog, tankeprotokoll, beteendeexperiment, och graderad exponering. Hjälp klienten identifiera tankefällor och utveckla copingstrategier.',
            priority: 8
          }
        }
      },
      'psychoanalytic': {
        name: 'Psykoanalytisk terapi',
        description: 'Utforskar omedvetna processer och tidiga livserfarenheter',
        instructions: {
          personality: {
            text: 'Var analytisk, lyssnade och tolkande. Upprätthåll en neutral men engagerad hållning. Använd tolkningar försiktigt och låt tystnad ha sitt utrymme.',
            priority: 8
          },
          approach: {
            text: 'Fokusera på överföring, motöverföring och omedvetna processer. Utforska barndomsupplevelser och deras koppling till nuvarande problem. Använd fria associationer.',
            priority: 9
          },
          boundaries: {
            text: 'Undvik att ge direkta råd eller lösningar. Tolka försiktigt och bara när klienten är redo. Respektera försvar och motstånd som viktiga terapeutiska fenomen.',
            priority: 8
          },
          techniques: {
            text: 'Använd tolkningar, drömanalys, fria associationer och överföringsanalys. Hjälp klienten upptäcka omedvetna mönster och tidiga relationserfarenheter.',
            priority: 7
          }
        }
      },
      'schema': {
        name: 'Schema Therapy',
        description: 'Integrerar CBT, psykodynamisk terapi och Gestalt för personlighetsstörningar',
        instructions: {
          personality: {
            text: 'Var närvarande, autentisk och känslomässigt tillgänglig. Använd begränsad återförsörjning och visa äkta empati. Balansera professionalism med mänsklig värme.',
            priority: 9
          },
          approach: {
            text: 'Identifiera tidiga maladaptiva scheman, copinglägen och föräldralägen. Använd schemamode-modellen och fokusera på känslomässig återförsörjning och läkning.',
            priority: 9
          },
          boundaries: {
            text: 'Var flexibel med traditionella terapeutiska gränser när det tjänar terapin. Använd begränsad återförsörjning men undvik dubbla relationer.',
            priority: 8
          },
          techniques: {
            text: 'Använd schema-fokuserade kognitiva och experientiella tekniker, mode-dialoger, tomstolstekniker och imagery rescripting för att läka barndomstrauman.',
            priority: 8
          }
        }
      },
      'act': {
        name: 'Acceptance and Commitment Therapy (ACT)',
        description: 'Fokuserar på psykologisk flexibilitet och värdebaserat levande',
        instructions: {
          personality: {
            text: 'Var öppen, nyfiken och accepterande. Använd metaforer och experientiella övningar. Modellera psykologisk flexibilitet och autenticitet.',
            priority: 8
          },
          approach: {
            text: 'Fokusera på de sex kärnprocesserna: acceptans, kognitiv defusion, närvaro, själv-som-kontext, värden och engagerat handlande. Minska experientiell undvikande.',
            priority: 9
          },
          boundaries: {
            text: 'Undvik att försöka eliminera eller kontrollera negativa tankar och känslor. Fokusera på funktion snarare än form av beteenden.',
            priority: 8
          },
          techniques: {
            text: 'Använd defusionstekniker, mindfulness-övningar, värdesklar ifieringsövningar, metaforer och experientiella övningar för att öka psykologisk flexibilitet.',
            priority: 8
          }
        }
      },
      'dbt': {
        name: 'Dialektisk Beteendeterapi (DBT)',
        description: 'Balanserar acceptans och förändring för emotionell dysreglering',
        instructions: {
          personality: {
            text: 'Var validerende, dialektisk och balanserad. Växla mellan acceptans och förändring. Var rakt på sak men varm och stödjande.',
            priority: 9
          },
          approach: {
            text: 'Fokusera på de fyra modulerna: mindfulness, interpersonell effektivitet, emotionsreglering och distress tolerance. Använd dialektiskt tänkande.',
            priority: 9
          },
          boundaries: {
            text: 'Balansera acceptans med förändringsstrategier. Validera först, utmana sedan. Håll fokus på säkerhet och riskreducering.',
            priority: 9
          },
          techniques: {
            text: 'Lär ut mindfulness, TIPP-tekniker, interpersonella färdigheter (DEAR MAN), emotionsregleringsstrategier och distress tolerance-färdigheter.',
            priority: 8
          }
        }
      }
    };
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">AI Kunskapsbas</h2>
        <p className="text-gray-600">
          Anpassa din AI-terapeut med specifik kunskap och instruktioner som kommer användas i alla dina klientsamtal.
        </p>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('instructions')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'instructions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon name="settings-2" size={16} className="inline mr-2" />
              AI Instruktioner
            </button>
            <button
              onClick={() => setActiveTab('knowledge')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'knowledge'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon name="square-library" size={16} className="inline mr-2" />
              Kunskapsbas
            </button>
          </nav>
        </div>

        {/* Content Area */}
        <div className="p-6">
          {/* Success/Error Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-800">❌ {error}</p>
            </div>
          )}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="text-green-800">✅ {success}</p>
            </div>
          )}

          {/* Knowledge Base Tab */}
          {activeTab === 'knowledge' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Kunskapsbas</h3>
                <button
                  onClick={() => setShowAddForm(!showAddForm)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                >
                  <Icon name="plus" size={16} className="inline mr-2" />
                  Lägg till kunskap
                </button>
              </div>

              {/* Add/Edit Knowledge Form */}
              {showAddForm && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <div className="mb-4">
                    <h4 className="text-lg font-medium text-gray-900 mb-2">
                      {editingItem ? 'Redigera kunskapsbas' : 'Lägg till ny kunskapsbas'}
                    </h4>
                    {!editingItem && (
                      <p className="text-sm text-gray-600">
                        Du kan antingen skriva kunskap direkt i textfältet eller ladda upp ett dokument som automatiskt extraherar innehållet. Dokumentuppladdning är valfritt.
                      </p>
                    )}
                  </div>
                  <form onSubmit={handleCreateKnowledge} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Titel *
                        </label>
                        <input
                          type="text"
                          value={knowledgeForm.title}
                          onChange={(e) => setKnowledgeForm({...knowledgeForm, title: e.target.value})}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="t.ex. Schema Therapy Grundprinciper"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Kategori
                        </label>
                        <div className="relative">
                          <select
                            value={knowledgeForm.category}
                            onChange={(e) => setKnowledgeForm({...knowledgeForm, category: e.target.value})}
                            className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-4 py-3 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors cursor-pointer hover:border-gray-400"
                          >
                            <option value="general">Allmänt</option>
                            <option value="therapy_approach">Terapiansats</option>
                            <option value="techniques">Tekniker</option>
                            <option value="guidelines">Riktlinjer</option>
                          </select>
                          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                            <Icon name="chevron-down" size={16} className="text-gray-400" />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Beskrivning
                      </label>
                      <input
                        type="text"
                        value={knowledgeForm.description}
                        onChange={(e) => setKnowledgeForm({...knowledgeForm, description: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Kort beskrivning av kunskapsområdet"
                      />
                    </div>

                    {/* Document Upload Section - Optional */}
                    {!editingItem && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Dokument (valfritt)
                        </label>
                        <div className="relative">
                          <input
                            type="file"
                            id="knowledge-document-upload"
                            onChange={(e) => setKnowledgeForm({...knowledgeForm, file: e.target.files[0]})}
                            accept=".txt,.pdf,.doc,.docx"
                            className="hidden"
                          />
                          <label
                            htmlFor="knowledge-document-upload"
                            className="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                            onDragOver={handleDragOver}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                          >
                            <div className="text-center">
                              <Icon name="upload" size={24} className="mx-auto text-gray-400 mb-2" />
                              <div className="text-sm text-gray-600">
                                {knowledgeForm.file ? (
                                  <span className="font-medium text-blue-600">{knowledgeForm.file.name}</span>
                                ) : (
                                  <>
                                    <span className="font-medium text-blue-600">Klicka för att välja fil</span>
                                    <span className="text-gray-500"> eller dra och släpp</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </label>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Stödda format: TXT, PDF, DOC, DOCX (max 10MB) • Dokumentet kommer att extrahera innehåll automatiskt
                        </p>
                        {knowledgeForm.file && (
                          <button
                            type="button"
                            onClick={() => setKnowledgeForm({...knowledgeForm, file: null})}
                            className="mt-2 text-sm text-red-600 hover:text-red-800 transition-colors"
                          >
                            <Icon name="x" size={14} className="inline mr-1" />
                            Ta bort fil
                          </button>
                        )}
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Innehåll {knowledgeForm.file ? '' : '*'}
                      </label>
                      {knowledgeForm.file && (
                        <p className="text-xs text-blue-600 mb-2">
                          💡 Eftersom du har valt en fil är textinnehållet valfritt. Filen kommer att användas som huvudkälla.
                        </p>
                      )}
                      <textarea
                        value={knowledgeForm.content}
                        onChange={(e) => setKnowledgeForm({...knowledgeForm, content: e.target.value})}
                        required={!knowledgeForm.file}
                        rows="6"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder={
                          knowledgeForm.file 
                            ? "Valfri ytterligare information som kompletterar dokumentet..."
                            : "Detaljerad information som AI:n ska använda i terapisamtal..."
                        }
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Prioritet
                        </label>
                        <div className="space-y-3">
                          <input
                            type="range"
                            min="1"
                            max="10"
                            value={knowledgeForm.priority}
                            onChange={(e) => setKnowledgeForm({...knowledgeForm, priority: parseInt(e.target.value)})}
                            className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 slider"
                            style={{
                              background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(knowledgeForm.priority - 1) * 11.11}%, #e5e7eb ${(knowledgeForm.priority - 1) * 11.11}%, #e5e7eb 100%)`
                            }}
                          />
                          <div className="flex justify-between text-xs text-gray-500">
                            <span>1 (Låg)</span>
                            <span className="font-medium text-blue-600 text-sm">{knowledgeForm.priority}</span>
                            <span>10 (Hög)</span>
                          </div>
                          <p className="text-xs text-gray-500">Högre prioritet = viktigare för AI:n</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddForm(false);
                          setEditingItem(null);
                          setKnowledgeForm({ title: '', description: '', content: '', category: 'general', priority: 1, file: null });
                        }}
                        className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                      >
                        Avbryt
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 transition-colors"
                      >
                        {loading ? 'Sparar...' : editingItem ? 'Uppdatera' : knowledgeForm.file ? 'Ladda upp dokument' : 'Spara kunskap'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Knowledge Entries List */}
              <div className="space-y-4">
                {knowledgeEntries.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Icon name="square-library" size={48} className="mx-auto text-gray-300 mb-4" />
                    <p>Ingen kunskapsbas skapad än.</p>
                    <p className="text-sm">Lägg till kunskap för att anpassa din AI-terapeut.</p>
                  </div>
                ) : (
                  knowledgeEntries.map((knowledge) => {
                    const categoryInfo = getCategoryInfo(knowledge.category);
                    return (
                      <div key={knowledge.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h4 className="text-lg font-medium text-gray-900">{knowledge.title}</h4>
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${categoryInfo.color}-100 text-${categoryInfo.color}-800`}>
                                <Icon name={categoryInfo.icon} size={12} className="mr-1" />
                                {categoryInfo.label}
                              </span>
                              {knowledge.description && knowledge.description.includes('Uploaded from') && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <Icon name="upload" size={12} className="mr-1" />
                                  Dokument
                                </span>
                              )}
                              <span className="text-xs text-gray-500">Prioritet: {knowledge.priority}</span>
                            </div>
                            {knowledge.description && (
                              <p className="text-sm text-gray-600 mb-2">{knowledge.description}</p>
                            )}
                            <div className="text-sm text-gray-700">
                              <p className="line-clamp-3">{knowledge.content}</p>
                              {knowledge.content && knowledge.content.length > 200 && (
                                <button
                                  onClick={() => handleViewContent(knowledge)}
                                  className="text-blue-600 hover:text-blue-800 text-xs mt-1 font-medium"
                                >
                                  Visa mer...
                                </button>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                              Skapad: {new Date(knowledge.created_at).toLocaleDateString('sv-SE')}
                            </p>
                          </div>
                          <div className="flex space-x-2 ml-4">
                            <button
                              onClick={() => handleViewContent(knowledge)}
                              className="p-2 text-gray-500 hover:text-green-600 transition-colors"
                              title="Visa fullständigt innehåll"
                            >
                              <Icon name="eye" size={16} />
                            </button>
                            <button
                              onClick={() => handleEditKnowledge(knowledge)}
                              className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
                              title="Redigera kunskapsbas"
                            >
                              <Icon name="edit" size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteKnowledge(knowledge.id)}
                              className="p-2 text-gray-500 hover:text-red-600 transition-colors"
                              title="Ta bort kunskapsbas"
                            >
                              <Icon name="trash" size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Content Viewing Modal */}
              {viewingContent && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                  <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full h-[85vh] flex flex-col overflow-hidden">
                    {/* Modal Header - Fixed */}
                    <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{viewingContent.title}</h3>
                        {viewingContent.description && (
                          <p className="text-sm text-gray-600 mt-1">{viewingContent.description}</p>
                        )}
                      </div>
                      <button
                        onClick={() => setViewingContent(null)}
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <Icon name="x" size={20} />
                      </button>
                    </div>
                    
                    {/* Modal Content - Scrollable */}
                    <div className="flex-1 overflow-y-auto p-6">
                      <div className="mb-4 flex items-center space-x-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${getCategoryInfo(viewingContent.category).color}-100 text-${getCategoryInfo(viewingContent.category).color}-800`}>
                          <Icon name={getCategoryInfo(viewingContent.category).icon} size={12} className="mr-1" />
                          {getCategoryInfo(viewingContent.category).label}
                        </span>
                        <span className="text-xs text-gray-500">Prioritet: {viewingContent.priority}</span>
                        <span className="text-xs text-gray-500">
                          Skapad: {new Date(viewingContent.created_at).toLocaleDateString('sv-SE')}
                        </span>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-gray-700">Innehåll:</h4>
                          {viewingContent.description && viewingContent.description.includes('Uploaded from') && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <Icon name="upload" size={12} className="mr-1" />
                              Extraherat från dokument
                            </span>
                          )}
                        </div>
                        <div className="whitespace-pre-wrap text-sm text-gray-900 leading-relaxed break-words">
                          {viewingContent.content}
                        </div>
                        {viewingContent.content && viewingContent.content.length > 5000 && (
                          <div className="text-xs text-gray-500 mt-4 pt-4 border-t border-gray-200 text-center">
                            Innehållet är {viewingContent.content.length.toLocaleString()} tecken långt
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Modal Footer - Fixed */}
                    <div className="p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={() => {
                            setViewingContent(null);
                            handleEditKnowledge(viewingContent);
                          }}
                          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                        >
                          <Icon name="edit" size={16} className="inline mr-2" />
                          Redigera
                        </button>
                        <button
                          onClick={() => setViewingContent(null)}
                          className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                        >
                          Stäng
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* AI Instructions Tab */}
          {activeTab === 'instructions' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">AI Instruktioner</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Definiera hur din AI-terapeut ska bete sig och kommunicera med dina klienter. Du kan ange en instruktion för varje kategori.
                </p>

                {/* Priority System Explanation */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
                    <Icon name="info" size={16} />
                    Prioritetssystem (1-10)
                  </h4>
                  <div className="text-sm text-gray-700 space-y-2">
                    <p><strong>Låg prioritet (1-3):</strong> Används som bakgrundsinformation, påverkar subtilt AI:ns svar.</p>
                    <p><strong>Medium prioritet (4-7):</strong> Balanserad vägning med annan information.</p>
                    <p><strong>Hög prioritet (8-10):</strong> Stark vägning, AI:n prioriterar denna information högt.</p>
                    <p className="text-xs text-gray-600 italic">Vid konflikter mellan instruktioner används högsta prioritet. Använd hög prioritet sparsamt för bästa resultat.</p>
                  </div>
                </div>

                {/* Template Selection */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2 flex items-center gap-2">
                    <Icon name="lightbulb" size={16} />
                    Terapiansats-mallar
                  </h4>
                  <p className="text-sm text-blue-800 mb-3">
                    Välj en professionell terapiansats för att automatiskt fylla i alla instruktioner baserat på etablerade terapeutiska principer.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {Object.entries(getTherapyTemplates()).map(([key, template]) => (
                      <button
                        key={key}
                        onClick={async () => {
                          if (window.confirm(`Är du säker på att du vill använda ${template.name} mall? Detta kommer att skriva över alla befintliga instruktioner.`)) {
                            setLoading(true);
                            setError('');
                            try {
                              // Apply template instructions
                              for (const [instructionType, instruction] of Object.entries(template.instructions)) {
                                const existingInstruction = aiInstructions.find(inst => inst.instruction_type === instructionType);
                                if (existingInstruction) {
                                  await handleUpdateInstructionFromTemplate(existingInstruction.id, instruction);
                                } else {
                                  await handleCreateInstructionFromTemplate(instructionType, instruction);
                                }
                              }
                              setSuccess(`${template.name} mall har tillämpats framgångsrikt!`);
                              await loadAiInstructions();
                            } catch (err) {
                              setError('Kunde inte tillämpa mallen. Försök igen.');
                            } finally {
                              setLoading(false);
                            }
                          }
                        }}
                        disabled={loading}
                        className="text-left p-3 bg-white border border-blue-200 rounded-md hover:bg-blue-50 hover:border-blue-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="font-medium text-blue-900 text-sm mb-1">{template.name}</div>
                        <div className="text-xs text-blue-700">{template.description}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Summary */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Sammanfattning</h4>
                <p className="text-sm text-gray-600">
                  {aiInstructions.length} av 4 instruktionstyper konfigurerade. 
                  AI:n kommer att använda dessa instruktioner när den analyserar sessioner och kommunicerar med dina klienter.
                </p>
              </div>

              </div>

              {/* Instructions List */}
              <div className="space-y-4">
                {['personality', 'approach', 'boundaries', 'techniques'].map((instructionType) => {
                  const typeInfo = getInstructionTypeInfo(instructionType);
                  const existingInstruction = aiInstructions.find(inst => inst.instruction_type === instructionType);
                  const isEditing = editingInstruction?.instruction_type === instructionType;
                  
                  return (
                    <div key={instructionType} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Card Header */}
                      <div className="bg-gray-50 p-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <Icon name={typeInfo.icon} size={20} className="text-blue-600" />
                            <div>
                              <h4 className="text-lg font-medium text-gray-900">{typeInfo.label}</h4>
                              <p className="text-sm text-gray-500">{typeInfo.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            {existingInstruction && (
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                <Icon name="check" size={12} className="mr-1" />
                                Konfigurerad
                              </span>
                            )}
                            {!existingInstruction && (
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                                <Icon name="clock" size={12} className="mr-1" />
                                Inte konfigurerad
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Card Content */}
                      <div className="p-4">
                        {!existingInstruction && !isEditing && (
                          // Empty state
                          <div className="text-center py-8">
                            <div className="mb-4">
                              <Icon name={typeInfo.icon} size={48} className="mx-auto text-gray-300 mb-3" />
                              <p className="text-gray-500 text-sm mb-4">Ingen instruktion för {typeInfo.label.toLowerCase()} än</p>
                            </div>
                            <button
                              onClick={() => {
                                setInstructionForm({ 
                                  instruction_type: instructionType, 
                                  instruction_text: '', 
                                  priority: 1 
                                });
                                setEditingInstruction({ instruction_type: instructionType });
                              }}
                              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center gap-2 mx-auto shadow-sm hover:shadow-md"
                            >
                              <Icon name="plus" size={16} />
                              Lägg till {typeInfo.label.toLowerCase()}
                            </button>
                          </div>
                        )}

                        {existingInstruction && !isEditing && (
                          // Existing instruction display
                          <div className="space-y-3">
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 relative">
                              <div className="flex items-start justify-between">
                                <div className="flex-1 mr-4">
                                  
                                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap mb-3">{existingInstruction.instruction_text}</p>
                                  <div className="flex items-center gap-1 mb-3">
                                    <Icon name="star" size={12} className="text-yellow-500" />
                                    <span className="text-xs text-gray-500">Prioritet: {existingInstruction.priority}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Icon name="clock" size={12} className="text-gray-400" />
                                    <span className="text-xs text-gray-500">Uppdaterad: {new Date(existingInstruction.updated_at).toLocaleDateString('sv-SE')}</span>
                                  </div>
                                </div>
                                <div className="flex space-x-2 flex-shrink-0">
                                  <button
                                    onClick={() => {
                                      setInstructionForm({
                                        instruction_type: existingInstruction.instruction_type,
                                        instruction_text: existingInstruction.instruction_text,
                                        priority: existingInstruction.priority
                                      });
                                      setEditingInstruction(existingInstruction);
                                    }}
                                    className="p-2 text-blue-600 hover:bg-blue-100 rounded-md transition-colors flex items-center justify-center"
                                    title="Redigera instruktion"
                                  >
                                    <Icon name="edit" size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteInstruction(existingInstruction.id, existingInstruction.instruction_type)}
                                    className="p-2 text-red-600 hover:bg-red-100 rounded-md transition-colors flex items-center justify-center"
                                    title="Ta bort instruktion"
                                  >
                                    <Icon name="trash" size={16} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {isEditing && (
                          // Edit form inline
                          <form onSubmit={handleCreateInstruction} className="space-y-4">
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Instruktion *
                                </label>
                                <textarea
                                  value={instructionForm.instruction_text}
                                  onChange={(e) => setInstructionForm({...instructionForm, instruction_text: e.target.value})}
                                  required
                                  rows="4"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                  placeholder={`Beskriv hur AI:n ska bete sig för ${typeInfo.label.toLowerCase()}...`}
                                />
                              </div>
                              
                              <div className="grid grid-cols-1 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Prioritet
                                  </label>
                                  <div className="space-y-3">
                                    <input
                                      type="range"
                                      min="1"
                                      max="10"
                                      value={instructionForm.priority}
                                      onChange={(e) => setInstructionForm({...instructionForm, priority: parseInt(e.target.value)})}
                                      className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 slider"
                                      style={{
                                        background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(instructionForm.priority - 1) * 11.11}%, #e5e7eb ${(instructionForm.priority - 1) * 11.11}%, #e5e7eb 100%)`
                                      }}
                                    />
                                    <div className="flex justify-between text-xs text-gray-500">
                                      <span>1 (Låg)</span>
                                      <span className="font-medium text-blue-600 text-sm">{instructionForm.priority}</span>
                                      <span>10 (Hög)</span>
                                    </div>
                                    <p className="text-xs text-gray-500">Högre prioritet = viktigare för AI:n</p>
                                  </div>
                                </div>
                                <div className="flex justify-end space-x-2 mt-4">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingInstruction(null);
                                      setInstructionForm({ instruction_type: 'personality', instruction_text: '', priority: 1 });
                                    }}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors flex items-center gap-2"
                                  >
                                    <Icon name="x" size={16} />
                                    Avbryt
                                  </button>
                                  <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                                  >
                                    <Icon name="check" size={16} />
                                    {loading ? 'Sparar...' : (editingInstruction && editingInstruction.id) ? 'Uppdatera' : 'Spara'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </form>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TherapistKnowledge;
